import { AdminScreenKey } from '../../types/admin-roles';

/** Spec 069 — everything on the audit and dashboard screens is read in Gulf Standard Time. */
export const DUBAI_TZ = 'Asia/Dubai';

const GST_DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: DUBAI_TZ, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
});
const GST_TIME = new Intl.DateTimeFormat('en-GB', { timeZone: DUBAI_TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const GST_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: DUBAI_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

/** "16 Oct 2026, 14:05 GST". */
export function formatGst(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${GST_DATE_TIME.format(d)} GST`;
}

/** "14:05 GST". */
export function formatGstTime(d: Date): string {
  return `${GST_TIME.format(d)} GST`;
}

/** The Dubai calendar day of an instant as `YYYY-MM-DD`. */
export function dubaiDay(d: Date = new Date()): string {
  return GST_DAY.format(d);
}

/** The Dubai calendar day `days` before today (Dubai has no DST, so 24 h steps are exact). */
export function dubaiDaysAgo(days: number, now: Date = new Date()): string {
  return dubaiDay(new Date(now.getTime() - days * 24 * 60 * 60 * 1000));
}

export const OVERDUE_MS = 48 * 60 * 60 * 1000;

/** "2d 4h", "5h 10m", "12m", "just now" — the age of the oldest waiting item. */
export function ageLabel(oldestAt: string | null | undefined, now: Date = new Date()): string | null {
  if (!oldestAt) return null;
  const t = new Date(oldestAt).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.floor((now.getTime() - t) / 60000));
  if (mins < 1) return 'just now';
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function isOverdue(oldestAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!oldestAt) return false;
  const t = new Date(oldestAt).getTime();
  return !Number.isNaN(t) && now.getTime() - t > OVERDUE_MS;
}

export function humanizeEntityType(type: string | null | undefined): string {
  if (!type) return '';
  const s = type.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function shortId(id: string | null | undefined): string {
  if (!id) return '';
  return id.length > 8 ? id.slice(-8) : id;
}

export interface EntityRef {
  entityType: string | null | undefined;
  entityId: string | null | undefined;
  garageId?: string | null;
  /** #1113 — the row's entity name; a CONFIG row's is the config key (platform `configTarget`). */
  entityName?: string | null;
  /** Raw `details` JSON of the row, when the caller has it (refund rows may carry the order id). */
  details?: string | null;
}

export interface EntityRoute {
  to: string;
  /** The screen the target page needs; the caller hides the link when the viewer lacks it. */
  screen: AdminScreenKey | null;
}

function detailString(details: string | null | undefined, ...keys: string[]): string | null {
  if (!details) return null;
  try {
    const parsed = JSON.parse(details) as Record<string, unknown>;
    for (const k of keys) {
      const v = parsed[k];
      if (typeof v === 'string' && v) return v;
    }
    return null;
  } catch {
    return null;
  }
}

function orderIdFromDetails(details: string | null | undefined): string | null {
  return detailString(details, 'orderId', 'order_id');
}

/**
 * Where an audited entity opens in the portal (AC-B2 "link when a screen exists"). Null when the
 * portal has no page for it. `screen: null` marks the super-admin-only roles page.
 */
/**
 * #1113 — where a config key is edited: the Finance cards' keys open the tab they live on, gated by
 * that tab's screen; every other key (or a row without its key) opens Runtime Config.
 */
const FINANCE_CONFIG_KEY = /^garagat\.(service-fee\.(enabled|percent|fixed-aed|cap-aed)|payment\.tabby\.(customer-enabled|pilot-customer-ids|fee-percent|fee-fixed-aed|fee-vat-inclusive|fee-bearer|fee-garage-base|min-order-amount|settlement-hold-days|payout-fee-aed))$/;
const ONE_WAY_KEY = 'garagat.delivery.one-way-legs-blocked';

function configRoute(key: string | null | undefined): EntityRoute {
  if (key && FINANCE_CONFIG_KEY.test(key)) return { to: '/finance?tab=configuration', screen: 'finance-config' };
  if (key === ONE_WAY_KEY) return { to: '/finance?tab=delivery', screen: 'finance' };
  return { to: '/dynamic-config', screen: 'dynamic-config' };
}

export function entityRoute({ entityType, entityId, entityName, garageId, details }: EntityRef): EntityRoute | null {
  const id = entityId ?? '';
  switch (entityType) {
    case 'GARAGE': return id ? { to: `/garages/${id}`, screen: 'garages' } : null;
    case 'GARAGE_DOCUMENT': return garageId ? { to: `/garages/${garageId}`, screen: 'garages' } : null;
    case 'ORDER': return id ? { to: `/orders/${id}`, screen: 'orders' } : null;
    case 'USER':
    case 'ADMIN_USER': {
      // USER rows carry the Keycloak subject id from #1071 on, the users-table id before. /users/:id
      // takes either (UserDetailPage falls back to the by-subject lookup); details.usersId wins when present.
      const target = detailString(details, 'usersId') ?? id;
      return target ? { to: `/users/${target}`, screen: 'users' } : null;
    }
    case 'ADMIN_ROLE': return { to: '/admin/roles', screen: null };
    case 'SETTLEMENT': return id ? { to: `/settlements/${id}`, screen: 'settlements' } : null;
    case 'PROVIDER_SETTLEMENT': return { to: '/settlements', screen: 'settlements' };
    case 'OFFERING':
    case 'SERVICE_OFFERING':
    case 'VARIANT':
      return garageId
        ? { to: `/garages/${garageId}/services`, screen: 'service-approvals' }
        : { to: '/service-approvals', screen: 'service-approvals' };
    case 'OFFER': return id ? { to: `/service-approvals/offers/${id}`, screen: 'service-approvals' } : null;
    case 'SERVICE_PROPOSAL': return { to: '/service-proposals', screen: 'service-approvals' };
    case 'CHANGE_REQUEST': return id ? { to: `/change-requests/${id}`, screen: 'change-requests' } : null;
    case 'REVIEW':
    case 'REVIEW_REPORT': return { to: '/reported-reviews', screen: 'reported-reviews' };
    case 'REFUND': {
      const orderId = orderIdFromDetails(details);
      return orderId ? { to: `/orders/${orderId}`, screen: 'orders' } : null;
    }
    case 'PROMO_CODE': return id ? { to: `/promotions/${id}`, screen: 'promotions' } : null;
    case 'PROMO_SETTINGS': return { to: '/finance?tab=configuration', screen: 'finance-config' };
    case 'DELIVERY_CONFIG':
    case 'RECOVERY_PROVIDER': return { to: '/finance?tab=delivery', screen: 'finance' };
    case 'PAYOUT': return { to: '/finance?tab=payouts', screen: 'finance' };
    case 'FINANCE_REPORT': return { to: '/finance?tab=reports', screen: 'finance' };
    case 'LOOKUP_ENTRY': return { to: '/lookups', screen: 'lookups' };
    case 'SERVICE_CATEGORY': return { to: '/categories', screen: 'service-catalog' };
    case 'TERMS_DOCUMENT': return { to: '/terms-documents', screen: 'terms-documents' };
    case 'PICKUP_SLOT': return { to: '/pickup-time-slots', screen: 'pickup-time-slots' };
    case 'TRANSLATION': return { to: '/translations', screen: 'translations' };
    case 'CONFIG': return configRoute(entityName ?? detailString(details, 'key', 'configKey'));
    default: return null;
  }
}

/** The route when the viewer may open it, else null. */
export function visibleEntityRoute(
  ref: EntityRef,
  isSuperAdmin: boolean,
  hasScreen: (screen: string) => boolean,
): string | null {
  const r = entityRoute(ref);
  if (!r) return null;
  if (r.screen === null) return isSuperAdmin ? r.to : null;
  return hasScreen(r.screen) ? r.to : null;
}
